/**
 * Pure state machine for in-place field editing (LANE ROLE-REFINE — parity
 * matrix gaps #6/#7: "the ability to refine the roles, and the roles you're
 * looking for within that role was missing"). Generic over BOTH the role
 * edit (PATCH /corpus/roles/:id) and the achievement edit (PATCH /corpus/
 * achievements/:id) — the lifecycle is identical for either: optimistic
 * local patch -> server call -> confirm (accept the server's row, since a
 * PATCH can normalize a field, e.g. isCurrent's 0/1 coercion) -> rollback on
 * failure (restore the pre-edit snapshot).
 *
 * Deliberately mirrors dragRehome.ts's shape (same three functions: apply/
 * confirm/rollback, same "keep the whole pre-change snapshot, not a diff"
 * discipline) rather than inventing a new pattern — this file's header note
 * on WHY optimistic+rollback is this app's house style for a write against
 * the corpus applies here unchanged. Kept framework-free (no React) so the
 * whole lifecycle is unit-testable without mounting a component.
 *
 * Generic over T (RoleRecord | AchievementRecord) rather than two near-
 * identical copies of this file — the state shape and lifecycle genuinely do
 * not vary by record type; only the id-matching field ('id' on both) and the
 * patch shape differ, and those are supplied by the caller (useCorpusCanvas),
 * not by this module.
 */

export interface FieldEditState<T> {
  /** The id of the record currently mid-save, or null when idle. */
  pendingId: string | null;
  /** The FULL pre-edit record, so a failure can restore it byte-for-byte
   *  rather than trying to reason about which fields changed. Null when idle. */
  snapshot: T | null;
}

export function initialFieldEditState<T>(): FieldEditState<T> {
  return { pendingId: null, snapshot: null };
}

/** Apply an optimistic field patch to the ONE record matching `id` inside a
 *  list, returning a NEW list (never mutates the input) plus the state needed
 *  to roll back. Mirrors applyOptimisticRehome's shape exactly. `getId` lets
 *  the caller supply how to read a record's id (both RoleRecord and
 *  AchievementRecord use `.id`, but this module makes no assumption about the
 *  shape of T beyond what the caller tells it). */
export function applyOptimisticEdit<T>(
  records: T[],
  id: string,
  getId: (r: T) => string,
  patch: Partial<T>,
): { next: T[]; state: FieldEditState<T> } {
  const original = records.find((r) => getId(r) === id) ?? null;
  const next = records.map((r) => (getId(r) === id ? { ...r, ...patch } : r));
  return {
    next,
    state: { pendingId: id, snapshot: original },
  };
}

/** The server confirmed — replace the optimistic row with the server's own
 *  return value (a PATCH can normalize a field the optimistic patch couldn't
 *  know, e.g. isCurrent's boolean->0|1 coercion, or updatedAt), and clear
 *  pending state. */
export function confirmFieldEdit<T>(
  records: T[],
  id: string,
  getId: (r: T) => string,
  serverRecord: T,
): { next: T[]; state: FieldEditState<T> } {
  const next = records.map((r) => (getId(r) === id ? serverRecord : r));
  return { next, state: initialFieldEditState<T>() };
}

/** The server rejected (or the request threw) — restore the pre-edit
 *  snapshot for that one record, keeping every other record's optimistic
 *  state (if any) untouched, and clear pending state. Returns `restored:
 *  null` if called with no pending snapshot (nothing to roll back — a caller
 *  bug, not a user-facing error) so the caller can decide how to log it
 *  without this module needing to throw. */
export function rollbackFieldEdit<T>(
  records: T[],
  getId: (r: T) => string,
  state: FieldEditState<T>,
): { restored: T[] | null; state: FieldEditState<T> } {
  if (state.snapshot === null || state.pendingId === null) {
    return { restored: null, state: initialFieldEditState<T>() };
  }
  const snapshot = state.snapshot;
  const pendingId = state.pendingId;
  const restored = records.map((r) => (getId(r) === pendingId ? snapshot : r));
  return { restored, state: initialFieldEditState<T>() };
}

/** Whether a record is currently mid-save (further edits on it disabled until
 *  the server confirms or rolls back — matches the drag pattern's "one
 *  mutation in flight at a time per record" UX). */
export function isFieldEditPending<T>(state: FieldEditState<T>, id: string): boolean {
  return state.pendingId === id;
}
